import { useState, useRef, useEffect, useCallback } from 'react';
import { Upload, X, Save, Camera, MapPin, Trash2, RefreshCw } from 'lucide-react';
import { FadeLoader } from 'react-spinners';
import toast from 'react-hot-toast';
import imageService from '../../../../services/imageHandlerAPI';
import lprJourneyAPI from '../../../../services/lprJourneyAPI';
import tutorialVideo from '../../../../images/camera-map-settings.mp4';
import './CameraSetupModal.css';

const SVG_WIDTH = 1000;
const SVG_HEIGHT = 700;
const NODE_RADIUS = 28;

/**
 * CameraSetupModal
 *
 * Props:
 *   networkId        {string}   - selected network ID
 *   cameras          {Array}    - cameras from CAMERAS table: [{ camera_id, camera_name, hwid, ... }]
 *   existingConfig   {object}   - { cameras: [...saved positions], hasBackgroundImage, isFullyConfigured }
 *   onSave           {function} - called after successful save
 *   onClose          {function} - called to close the modal (only when already configured)
 *   isRequired       {boolean}  - if true, user cannot dismiss without saving
 */
const CameraSetupModal = ({
  networkId,
  cameras = [],
  existingConfig = null,
  onSave,
  onClose,
  isRequired = false
}) => {
  // ─── Background image state ───────────────────────────────────────────────
  const [backgroundImageUrl, setBackgroundImageUrl] = useState(null);
  const [hasBackgroundImage, setHasBackgroundImage] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [showImageUpload, setShowImageUpload] = useState(false);

  // ─── Camera placement state ───────────────────────────────────────────────
  // cameraPositions: { [camera_id]: { x, y } } — normalized 0..1
  const [cameraPositions, setCameraPositions] = useState({});
  const [draggedCamera, setDraggedCamera] = useState(null);
  const [isDraggingFromSidebar, setIsDraggingFromSidebar] = useState(false);
  const [mousePosition, setMousePosition] = useState(null);
  const [hoveredCamera, setHoveredCamera] = useState(null);

  // ─── Tutorial state ────────────────────────────────────────────────────────
  const [showTutorial, setShowTutorial] = useState(false);

  // ─── Saving state ─────────────────────────────────────────────────────────
  const [isSaving, setIsSaving] = useState(false);

  const svgRef = useRef(null);
  const fileInputRef = useRef(null);
  const modalRef = useRef(null);
  const imageUploadModalRef = useRef(null);

  // ─── Wasabi image key for this network ────────────────────────────────────
  const imageKey = `lpr-journey/${networkId}`;

  // ─── Load existing config on mount ────────────────────────────────────────
  useEffect(() => {
    if (!existingConfig) return;

    // Restore saved positions (stored normalized)
    const positions = {};
    existingConfig.cameras.forEach(cam => {
      if (cam.pos_x !== null && cam.pos_y !== null) {
        positions[cam.camera_id] = { x: cam.pos_x, y: cam.pos_y };
      }
    });
    setCameraPositions(positions);

    // Load background image if it exists
    if (existingConfig.hasBackgroundImage) {
      setHasBackgroundImage(true);
      loadBackgroundImage();
    }
  }, [existingConfig, networkId]);

  const loadBackgroundImage = async () => {
    setIsImageLoading(true);
    try {
      const result = await imageService.getImageUrl(imageKey, 7200);
      if (result.success) {
        setBackgroundImageUrl(result.url);
      }
    } catch (error) {
      console.error('Error loading background image:', error);
    } finally {
      setIsImageLoading(false);
    }
  };

  // ─── SVG coordinate helpers ───────────────────────────────────────────────
  const getSVGCoordinates = (e) => {
    if (!svgRef.current) return null;
    const svg = svgRef.current;
    const pt = svg.createSVGPoint();
    pt.x = e.clientX;
    pt.y = e.clientY;
    return pt.matrixTransform(svg.getScreenCTM().inverse());
  };

  const normalizePos = (pos) => ({
    x: Math.round((Math.max(0, Math.min(pos.x, SVG_WIDTH)) / SVG_WIDTH) * 10000) / 10000,
    y: Math.round((Math.max(0, Math.min(pos.y, SVG_HEIGHT)) / SVG_HEIGHT) * 10000) / 10000
  });

  const denormalizePos = (pos) => ({
    x: pos.x * SVG_WIDTH,
    y: pos.y * SVG_HEIGHT
  });

  // ─── Drag handlers ────────────────────────────────────────────────────────
  const handleSVGMouseMove = (e) => {
    const svgP = getSVGCoordinates(e);
    if (!svgP) return;

    if (isDraggingFromSidebar) {
      setMousePosition(svgP);
    }

    if (draggedCamera && !isDraggingFromSidebar) {
      const clamped = {
        x: Math.max(NODE_RADIUS, Math.min(svgP.x, SVG_WIDTH - NODE_RADIUS)),
        y: Math.max(NODE_RADIUS, Math.min(svgP.y, SVG_HEIGHT - NODE_RADIUS))
      };
      setCameraPositions(prev => ({
        ...prev,
        [draggedCamera]: normalizePos(clamped)
      }));
    }
  };

  const handleSVGMouseUp = (e) => {
    if (isDraggingFromSidebar && draggedCamera) {
      const svgP = getSVGCoordinates(e);
      if (svgP) {
        const clamped = {
          x: Math.max(NODE_RADIUS, Math.min(svgP.x, SVG_WIDTH - NODE_RADIUS)),
          y: Math.max(NODE_RADIUS, Math.min(svgP.y, SVG_HEIGHT - NODE_RADIUS))
        };
        setCameraPositions(prev => ({
          ...prev,
          [draggedCamera]: normalizePos(clamped)
        }));
      }
    }
    setDraggedCamera(null);
    setIsDraggingFromSidebar(false);
    setMousePosition(null);
  };

  const handleSVGDrop = (e) => {
    e.preventDefault();
    if (isDraggingFromSidebar && draggedCamera) {
      const svgP = getSVGCoordinates(e);
      if (svgP) {
        const clamped = {
          x: Math.max(NODE_RADIUS, Math.min(svgP.x, SVG_WIDTH - NODE_RADIUS)),
          y: Math.max(NODE_RADIUS, Math.min(svgP.y, SVG_HEIGHT - NODE_RADIUS))
        };
        setCameraPositions(prev => ({
          ...prev,
          [draggedCamera]: normalizePos(clamped)
        }));
      }
    }
    setIsDraggingFromSidebar(false);
    setDraggedCamera(null);
  };

  const handleNodeMouseDown = (e, cameraId) => {
    e.preventDefault();
    setDraggedCamera(cameraId);
    setIsDraggingFromSidebar(false);
  };

  const handleSidebarDragStart = (e, cameraId) => {
    setDraggedCamera(cameraId);
    setIsDraggingFromSidebar(true);
  };

  const handleRemoveCamera = (cameraId) => {
    setCameraPositions(prev => {
      const next = { ...prev };
      delete next[cameraId];
      return next;
    });
    if (hoveredCamera === cameraId) setHoveredCamera(null);
  };

  const handleAutoPlace = () => {
    const positions = {};
    cameras.forEach((cam, i) => {
      const angle = (i * 2 * Math.PI) / cameras.length;
      const radius = Math.min(SVG_WIDTH, SVG_HEIGHT) / 2 - 100;
      positions[cam.camera_id] = normalizePos({
        x: SVG_WIDTH / 2 + radius * Math.cos(angle),
        y: SVG_HEIGHT / 2 + radius * Math.sin(angle)
      });
    });
    setCameraPositions(positions);
  };

  // ─── Image upload ─────────────────────────────────────────────────────────
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    setIsImageUploading(true);
    try {
      // Upload with the network-scoped key
      const uploadResult = await imageService.uploadImage(file, 'lpr-journey', networkId);
      if (uploadResult.success) {
        const urlResult = await imageService.getImageUrl(imageKey, 7200);
        if (urlResult.success) {
          setBackgroundImageUrl(urlResult.url);
          setHasBackgroundImage(true);
          setShowImageUpload(false);
          toast.success('Background image uploaded successfully');
        }
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image: ' + error.message);
    } finally {
      setIsImageUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleImageDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file || !file.type.startsWith('image/')) {
      toast.error('Please drop a valid image file');
      return;
    }
    const fakeEvent = { target: { files: [file] } };
    await handleFileUpload(fakeEvent);
  };

  const handleRemoveBackgroundImage = async () => {
    try {
      await imageService.deleteImage(imageKey);
      toast.success('Background image removed');
    } catch (error) {
      // Ignore "not found" errors
      if (!error.message?.includes('not found')) {
        toast.error('Failed to remove image from storage');
      }
    }
    setBackgroundImageUrl(null);
    setHasBackgroundImage(false);
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (Object.keys(cameraPositions).length === 0) {
      toast.error('Please place at least one camera on the map');
      return;
    }

    setIsSaving(true);
    try {
      const positionsToSave = cameras
        .filter(cam => cameraPositions[cam.camera_id])
        .map(cam => ({
          camera_id: cam.camera_id,
          hwid: cam.hwid,
          camera_name: cam.camera_name,
          pos_x: cameraPositions[cam.camera_id].x,
          pos_y: cameraPositions[cam.camera_id].y
        }));

      await lprJourneyAPI.saveConfig(networkId, positionsToSave, hasBackgroundImage);
      toast.success('Camera map configuration saved successfully');
      if (onSave) onSave();
    } catch (error) {
      console.error('Error saving config:', error);
      toast.error('Failed to save configuration: ' + error.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ─── Derived state ────────────────────────────────────────────────────────
  const placedCameraIds = new Set(Object.keys(cameraPositions));
  const unplacedCameras = cameras.filter(c => !placedCameraIds.has(c.camera_id));
  const placedCount = placedCameraIds.size;
  const totalCount = cameras.length;
  const allPlaced = placedCount === totalCount && totalCount > 0;

  // ─── Camera type helpers ──────────────────────────────────────────────────
  const getCameraType = (cam) => {
    if (cam.is_entry_point === 'true') return 'entry';
    if (cam.is_exit_point === 'true') return 'exit';
    return 'regular';
  };

  const CAMERA_COLORS = {
    entry: {
      fill: '#16a34a',
      fillDragging: '#22c55e',
      stroke: 'white',
      strokeHovered: '#86efac',
      glow: 'rgba(34,197,94,0.45)',
      ring: '#4ade80',
      label: 'ENTRY',
      icon: '▶',
    },
    exit: {
      fill: '#b45309',
      fillDragging: '#d97706',
      stroke: 'white',
      strokeHovered: '#fcd34d',
      glow: 'rgba(217,119,6,0.45)',
      ring: '#fbbf24',
      label: 'EXIT',
      icon: '◀',
    },
    regular: {
      fill: '#4f46e5',
      fillDragging: '#6366f1',
      stroke: 'white',
      strokeHovered: '#a5b4fc',
      glow: null,
      ring: null,
      label: null,
      icon: '📷',
    },
  };

  // ─── Render camera node on SVG ────────────────────────────────────────────
  const renderCameraNode = (cam) => {
    const normPos = cameraPositions[cam.camera_id];
    if (!normPos) return null;
    const pos = denormalizePos(normPos);
    const isHovered = hoveredCamera === cam.camera_id;
    const isDragging = draggedCamera === cam.camera_id && !isDraggingFromSidebar;
    const type = getCameraType(cam);
    const colors = CAMERA_COLORS[type];

    return (
      <g
        key={cam.camera_id}
        onMouseEnter={() => setHoveredCamera(cam.camera_id)}
        onMouseLeave={() => setHoveredCamera(null)}
      >
        {/* Glow ring for entry/exit */}
        {colors.glow && (
          <circle
            cx={pos.x}
            cy={pos.y}
            r={NODE_RADIUS + 7}
            fill={colors.glow}
            style={{ pointerEvents: 'none' }}
          />
        )}
        {/* Dashed outer ring for entry/exit */}
        {colors.ring && (
          <circle
            cx={pos.x}
            cy={pos.y}
            r={NODE_RADIUS + 5}
            fill="none"
            stroke={colors.ring}
            strokeWidth="1.5"
            strokeDasharray="4 3"
            style={{ pointerEvents: 'none' }}
          />
        )}
        {/* Shadow */}
        <circle cx={pos.x + 2} cy={pos.y + 2} r={NODE_RADIUS} fill="rgba(0,0,0,0.35)" />
        {/* Main circle */}
        <circle
          cx={pos.x}
          cy={pos.y}
          r={NODE_RADIUS}
          fill={isDragging ? colors.fillDragging : colors.fill}
          stroke={isHovered ? colors.strokeHovered : colors.stroke}
          strokeWidth={isHovered ? 3 : 2}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
          onMouseDown={(e) => handleNodeMouseDown(e, cam.camera_id)}
        />
        {/* Icon */}
        <text
          x={pos.x}
          y={pos.y - 5}
          fill="white"
          fontSize={type === 'regular' ? '14' : '11'}
          textAnchor="middle"
          dominantBaseline="central"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {colors.icon}
        </text>
        {/* Type label (ENTRY / EXIT) inside node */}
        {colors.label && (
          <text
            x={pos.x}
            y={pos.y + 10}
            fill="white"
            fontSize="7"
            fontWeight="800"
            letterSpacing="0.5"
            textAnchor="middle"
            dominantBaseline="central"
            style={{ pointerEvents: 'none', userSelect: 'none' }}
          >
            {colors.label}
          </text>
        )}
        {/* Camera name below node */}
        <text
          x={pos.x}
          y={pos.y + NODE_RADIUS + (colors.ring ? 12 : 10)}
          fill={type === 'regular' ? 'rgba(255,255,255,0.85)' : colors.ring || 'white'}
          fontSize="9"
          fontWeight="600"
          textAnchor="middle"
          dominantBaseline="central"
          style={{ pointerEvents: 'none', userSelect: 'none' }}
        >
          {cam.camera_name.length > 12 ? cam.camera_name.slice(0, 11) + '…' : cam.camera_name}
        </text>

        {/* Remove button on hover */}
        {isHovered && (
          <g>
            <circle
              cx={pos.x + NODE_RADIUS - 2}
              cy={pos.y - NODE_RADIUS + 2}
              r={10}
              fill="#ef4444"
              stroke="white"
              strokeWidth="1.5"
              style={{ cursor: 'pointer' }}
              onMouseDown={(e) => { e.stopPropagation(); handleRemoveCamera(cam.camera_id); }}
            />
            <text
              x={pos.x + NODE_RADIUS - 2}
              y={pos.y - NODE_RADIUS + 2}
              fill="white"
              fontSize="12"
              fontWeight="bold"
              textAnchor="middle"
              dominantBaseline="central"
              style={{ pointerEvents: 'none', userSelect: 'none' }}
            >
              ×
            </text>
          </g>
        )}
      </g>
    );
  };

  return (
    <div
      className="lpr-setup-overlay"
      onClick={!isRequired ? onClose : undefined}
    >
      <div className="lpr-setup-modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="lpr-setup-header">
          <div className="lpr-setup-header-left">
            <MapPin size={20} className="lpr-setup-header-icon" />
            <div>
              <h2 className="lpr-setup-title">Camera Map Setup</h2>
              <p className="lpr-setup-subtitle">
                Place cameras on the map to define their physical locations
              </p>
            </div>
          </div>
          <div className="lpr-setup-header-right">
            <span className="lpr-setup-progress">
              {placedCount} / {totalCount} cameras placed
            </span>
            {!isRequired && (
              <button className="lpr-setup-close-btn" onClick={onClose} title="Close">
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {/* ── Progress bar ───────────────────────────────────────────────── */}
        <div className="lpr-setup-progress-bar-container">
          <div
            className="lpr-setup-progress-bar-fill"
            style={{ width: totalCount > 0 ? `${(placedCount / totalCount) * 100}%` : '0%' }}
          />
        </div>

        {/* ── Body ───────────────────────────────────────────────────────── */}
        <div className="lpr-setup-body">
          {/* ── Sidebar ──────────────────────────────────────────────────── */}
          <div className="lpr-setup-sidebar">
            <div className="lpr-setup-sidebar-section">
              <div className="lpr-setup-sidebar-section-header">
                <h3 className="lpr-setup-sidebar-title">
                  <Camera size={14} />
                  Cameras
                </h3>
                {unplacedCameras.length < cameras.length && cameras.length > 0 && (
                  <button
                    className="lpr-setup-auto-place-btn"
                    onClick={handleAutoPlace}
                    title="Auto-place all cameras in a circle"
                  >
                    <RefreshCw size={12} />
                    Auto-place all
                  </button>
                )}
              </div>

              {/* Unplaced cameras */}
              {unplacedCameras.length > 0 && (
                <div className="lpr-setup-camera-list">
                  <p className="lpr-setup-camera-list-label">Drag onto map:</p>
                  {unplacedCameras.map(cam => {
                    const type = getCameraType(cam);
                    return (
                      <div
                        key={cam.camera_id}
                        className={`lpr-setup-camera-chip lpr-setup-camera-chip-unplaced lpr-setup-camera-chip-${type}`}
                        draggable
                        onDragStart={(e) => handleSidebarDragStart(e, cam.camera_id)}
                        onDragEnd={() => { setIsDraggingFromSidebar(false); setDraggedCamera(null); }}
                      >
                        <span className="lpr-setup-camera-chip-icon">
                          {type === 'entry' ? '▶' : type === 'exit' ? '◀' : '📷'}
                        </span>
                        <span className="lpr-setup-camera-chip-name">{cam.camera_name}</span>
                        {type !== 'regular' && (
                          <span className={`lpr-setup-camera-chip-badge lpr-setup-camera-chip-badge-${type}`}>
                            {type === 'entry' ? 'Entry' : 'Exit'}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Placed cameras */}
              {placedCount > 0 && (
                <div className="lpr-setup-camera-list" style={{ marginTop: '12px' }}>
                  <p className="lpr-setup-camera-list-label">Placed ({placedCount}):</p>
                  {cameras.filter(c => placedCameraIds.has(c.camera_id)).map(cam => {
                    const type = getCameraType(cam);
                    return (
                      <div
                        key={cam.camera_id}
                        className={`lpr-setup-camera-chip lpr-setup-camera-chip-placed lpr-setup-camera-chip-placed-${type}`}
                      >
                        <span className="lpr-setup-camera-chip-icon">
                          {type === 'entry' ? '▶' : type === 'exit' ? '◀' : '📍'}
                        </span>
                        <span className="lpr-setup-camera-chip-name">{cam.camera_name}</span>
                        {type !== 'regular' && (
                          <span className={`lpr-setup-camera-chip-badge lpr-setup-camera-chip-badge-${type}`}>
                            {type === 'entry' ? 'Entry' : 'Exit'}
                          </span>
                        )}
                        <button
                          className="lpr-setup-camera-chip-remove"
                          onClick={() => handleRemoveCamera(cam.camera_id)}
                          title="Remove from map"
                        >
                          <X size={10} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Background image controls */}
            <div className="lpr-setup-sidebar-section">
              <h3 className="lpr-setup-sidebar-title">
                <Upload size={14} />
                Background Map
              </h3>
              <button
                className="lpr-setup-bg-btn"
                onClick={() => setShowImageUpload(true)}
              >
                <Upload size={14} />
                {hasBackgroundImage ? 'Replace Image' : 'Upload Map Image'}
              </button>
              {hasBackgroundImage && backgroundImageUrl && (
                <div className="lpr-setup-bg-preview">
                  <img src={backgroundImageUrl} alt="Map background" className="lpr-setup-bg-thumb" />
                  <button
                    className="lpr-setup-bg-remove-btn"
                    onClick={handleRemoveBackgroundImage}
                    title="Remove background image"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
              {!hasBackgroundImage && (
                <p className="lpr-setup-bg-hint">
                  Upload a street map or floor plan as the background
                </p>
              )}
            </div>
          </div>

          {/* ── Canvas ───────────────────────────────────────────────────── */}
          <div className="lpr-setup-canvas-area">
            {showTutorial && (
              <div className="lpr-tutorial-overlay">
                <button className="lpr-tutorial-close" onClick={() => setShowTutorial(false)}>
                  <X size={16} />
                </button>
                <video
                  className="lpr-tutorial-video"
                  src={tutorialVideo}
                  autoPlay
                  controls
                  onEnded={() => setShowTutorial(false)}
                />
              </div>
            )}
            <svg
              ref={svgRef}
              width="100%"
              height="100%"
              viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
              preserveAspectRatio="xMidYMid meet"
              className="lpr-setup-svg"
              onMouseMove={handleSVGMouseMove}
              onMouseUp={handleSVGMouseUp}
              onMouseLeave={handleSVGMouseUp}
              onDrop={handleSVGDrop}
              onDragOver={(e) => e.preventDefault()}
            >
              {/* Background image */}
              {backgroundImageUrl && (
                <image
                  href={backgroundImageUrl}
                  x="0"
                  y="0"
                  width={SVG_WIDTH}
                  height={SVG_HEIGHT}
                  opacity={isImageLoading ? '0.3' : '0.75'}
                  preserveAspectRatio="xMidYMid meet"
                  onLoad={() => setIsImageLoading(false)}
                  onError={() => setIsImageLoading(false)}
                />
              )}

              {/* Grid overlay when no background */}
              {!backgroundImageUrl && (
                <>
                  <rect x="0" y="0" width={SVG_WIDTH} height={SVG_HEIGHT} fill="rgba(15,23,42,0.6)" />
                  {/* Grid lines */}
                  {Array.from({ length: 10 }, (_, i) => (
                    <line
                      key={`v${i}`}
                      x1={(i + 1) * (SVG_WIDTH / 10)} y1="0"
                      x2={(i + 1) * (SVG_WIDTH / 10)} y2={SVG_HEIGHT}
                      stroke="rgba(255,255,255,0.05)" strokeWidth="1"
                    />
                  ))}
                  {Array.from({ length: 7 }, (_, i) => (
                    <line
                      key={`h${i}`}
                      x1="0" y1={(i + 1) * (SVG_HEIGHT / 7)}
                      x2={SVG_WIDTH} y2={(i + 1) * (SVG_HEIGHT / 7)}
                      stroke="rgba(255,255,255,0.05)" strokeWidth="1"
                    />
                  ))}
                  {/* Empty state hint */}
                  {placedCount === 0 && (
                    <>
                      <text
                        x={SVG_WIDTH / 2}
                        y={SVG_HEIGHT / 2}
                        fill="rgba(255,255,255,0.3)"
                        fontSize="18"
                        textAnchor="middle"
                        dominantBaseline="central"
                      >
                        Drag cameras here or upload a map image
                      </text>
                      <g style={{ cursor: 'pointer' }} onClick={() => setShowTutorial(true)}>
                        <rect
                          x={SVG_WIDTH / 2 - 70}
                          y={SVG_HEIGHT / 2 + 28}
                          width={140}
                          height={34}
                          rx={8}
                          fill="rgba(99,102,241,0.18)"
                          stroke="rgba(99,102,241,0.5)"
                          strokeWidth="1.5"
                        />
                        <text
                          x={SVG_WIDTH / 2}
                          y={SVG_HEIGHT / 2 + 45}
                          fill="rgba(165,180,252,0.9)"
                          fontSize="13"
                          fontWeight="600"
                          textAnchor="middle"
                          dominantBaseline="central"
                          style={{ pointerEvents: 'none', userSelect: 'none' }}
                        >
                          ▶ Play Tutorial
                        </text>
                      </g>
                    </>
                  )}
                </>
              )}

              {/* Drag preview */}
              {isDraggingFromSidebar && draggedCamera && mousePosition && (() => {
                const dragCam = cameras.find(c => c.camera_id === draggedCamera);
                const dragType = dragCam ? getCameraType(dragCam) : 'regular';
                const dragColors = CAMERA_COLORS[dragType];
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <circle
                      cx={mousePosition.x}
                      cy={mousePosition.y}
                      r={NODE_RADIUS}
                      fill={dragColors.fill + 'aa'}
                      stroke="white"
                      strokeWidth="2"
                      strokeDasharray="4 2"
                    />
                    <text
                      x={mousePosition.x}
                      y={mousePosition.y}
                      fill="white"
                      fontSize={dragType === 'regular' ? '14' : '11'}
                      textAnchor="middle"
                      dominantBaseline="central"
                    >
                      {dragColors.icon}
                    </text>
                  </g>
                );
              })()}

              {/* Legend */}
              {(() => {
                const hasEntry = cameras.some(c => c.is_entry_point === 'true');
                const hasExit = cameras.some(c => c.is_exit_point === 'true');
                if (!hasEntry && !hasExit) return null;
                const items = [
                  ...(hasEntry ? [{ color: '#16a34a', ring: '#4ade80', label: 'Entry Camera', icon: '▶' }] : []),
                  ...(hasExit  ? [{ color: '#b45309', ring: '#fbbf24', label: 'Exit Camera',  icon: '◀' }] : []),
                  { color: '#4f46e5', ring: null, label: 'Regular Camera', icon: '📷' },
                ];
                const legendX = SVG_WIDTH - 16;
                const legendY = SVG_HEIGHT - 16;
                const itemH = 22;
                const boxH = items.length * itemH + 16;
                const boxW = 160;
                return (
                  <g style={{ pointerEvents: 'none' }}>
                    <rect
                      x={legendX - boxW}
                      y={legendY - boxH}
                      width={boxW}
                      height={boxH}
                      rx="6"
                      fill="rgba(10,15,30,0.82)"
                      stroke="rgba(255,255,255,0.12)"
                      strokeWidth="1"
                    />
                    {items.map((item, i) => {
                      const iy = legendY - boxH + 10 + i * itemH;
                      const ix = legendX - boxW + 12;
                      return (
                        <g key={item.label}>
                          <circle cx={ix + 8} cy={iy + 6} r={8} fill={item.color} stroke={item.ring || 'rgba(255,255,255,0.3)'} strokeWidth="1.5" />
                          <text x={ix + 8} y={iy + 6} fill="white" fontSize="7" textAnchor="middle" dominantBaseline="central">{item.icon}</text>
                          <text x={ix + 22} y={iy + 6} fill="rgba(255,255,255,0.8)" fontSize="10" dominantBaseline="central">{item.label}</text>
                        </g>
                      );
                    })}
                  </g>
                );
              })()}

              {/* Camera nodes */}
              {cameras.map(cam => renderCameraNode(cam))}
            </svg>
          </div>
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="lpr-setup-footer">
          <div className="lpr-setup-footer-info">
            {allPlaced ? (
              <span className="lpr-setup-footer-all-placed">✓ All cameras placed</span>
            ) : (
              <span className="lpr-setup-footer-partial">
                {unplacedCameras.length} camera{unplacedCameras.length !== 1 ? 's' : ''} not yet placed
              </span>
            )}
          </div>
          <div className="lpr-setup-footer-actions">
            {!isRequired && (
              <button className="lpr-setup-cancel-btn" onClick={onClose}>
                Cancel
              </button>
            )}
            <button
              className="lpr-setup-save-btn"
              onClick={handleSave}
              disabled={isSaving || placedCount === 0}
            >
              <Save size={14} />
              {isSaving ? 'Saving…' : 'Save Configuration'}
            </button>
          </div>
        </div>
      </div>

      {/* ── Image Upload Sub-Modal ──────────────────────────────────────────── */}
      {showImageUpload && (
        <div
          className="lpr-setup-image-modal-overlay"
          onClick={() => setShowImageUpload(false)}
        >
          <div className="lpr-setup-image-modal" ref={imageUploadModalRef} onClick={(e) => e.stopPropagation()}>
            <button
              className="lpr-setup-image-modal-close"
              onClick={() => setShowImageUpload(false)}
            >
              <X size={16} />
            </button>
            <h3 className="lpr-setup-image-modal-title">Upload Map Background</h3>
            <div
              className="lpr-setup-image-dropzone"
              onDrop={handleImageDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current?.click()}
            >
              {isImageUploading ? (
                <div className="lpr-setup-image-uploading">
                  <FadeLoader color="white" />
                  <p>Uploading…</p>
                </div>
              ) : (
                <>
                  <Upload size={40} className="lpr-setup-image-dropzone-icon" />
                  <p className="lpr-setup-image-dropzone-text">
                    Drop an image here or click to select
                  </p>
                  <p className="lpr-setup-image-dropzone-subtext">
                    PNG, JPG, WebP — street maps, floor plans, etc.
                  </p>
                </>
              )}
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CameraSetupModal;
